import { useEffect, useRef } from 'react'
import {
  VStack,
  Box,
  AspectRatio,
  Divider,
  Text,
  Link,
  Flex,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import Image from 'next/image'
import { useRouter } from 'next/router'

import Banner from '@/features/blogs/components/Banner'
import FacebookShareButton from '@/features/blogs/components/FacebookShareButton'

import { getLocaleString } from '@/lib/utils/format/datetime'

import { MarkdownStyle } from './styles'

import { Blog } from '@/features/blogs/types/blog'
import Tag from '@/features/blogs/components/BlogItem/Tag'
import { SuggestionBlogs } from '@/features/blogs/components/SuggestionBlogs'

interface Props {
  id?: string
  blog: Blog
}

function BlogContent({ id, blog }: Props): React.ReactElement {
  const contentRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  useEffect(() => {
    if (contentRef.current) {
      const handleIntersect: IntersectionObserverCallback = (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio > 0) {
            entry.target.classList.toggle('visible', true)
            observer.unobserve(entry.target)
          }
        })
      }

      const observer = new IntersectionObserver(handleIntersect, {
        threshold: 0.1,
      })

      for (const child of contentRef.current.children) {
        observer.observe(child)
      }

      return () => observer.disconnect()
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {}
  }, [contentRef.current])

  return (
    <VStack
      id={id}
      spacing="6"
      layerStyle="container"
      sx={{
        alignItems: 'stretch',
        maxWidth: { md: '860px' },
        pb: 0,
        pt: {
          base: 14,
          sm: 20,
        },
      }}
    >
      <VStack
        id={id}
        spacing="2"
        sx={{
          alignItems: 'stretch',
          textAlign: 'left',
        }}
      >
        <Link
          as="button"
          sx={{
            textAlign: 'inherit',
            fontWeight: 'light',
          }}
          onClick={() => {
            router.back()
          }}
        >
          กลับ
        </Link>
        <Text
          as="h1"
          sx={{
            fontSize: { base: '3.75xl', md: '4xl' },
            fontWeight: 'medium',
          }}
        >
          {blog.title}
        </Text>
        <Flex justify="space-between">
          <Text
            sx={{
              fontSize: 'sm',
              fontWeight: 'medium',
              color: 'primary.400',
            }}
          >
            {getLocaleString(new Date(blog.published_at))}
          </Text>
          <FacebookShareButton />
        </Flex>
      </VStack>
      <AspectRatio ratio={264 / 138}>
        <Image
          priority
          title={blog.title}
          src={blog.image.original}
          alt={blog.title}
          placeholder="blur"
          blurDataURL={blog.image.thumbnail ?? blog.image.original}
          objectFit="cover"
          layout="fill"
        />
      </AspectRatio>
      <Box
        ref={contentRef}
        sx={MarkdownStyle}
        dangerouslySetInnerHTML={{ __html: blog.content }}
      />
      <Box>
        <FacebookShareButton />
      </Box>
      <Wrap>
        {blog.tags.map((tag, idx: number) => (
          <WrapItem key={idx}>
            <Tag {...tag} />
          </WrapItem>
        ))}
      </Wrap>
      <Banner />
      <Divider
        sx={{
          pt: 8,
          borderColor: 'secondary.800',
          opacity: 0.2,
        }}
      />
      <SuggestionBlogs blogs={blog.recommended || []} />
      <Divider
        sx={{
          pt: 12,
          borderColor: 'secondary.800',
          opacity: 0.2,
        }}
      />
    </VStack>
  )
}

export default BlogContent
